// frontend/src/services/applicationInsights.js
// Azure Application Insights for React frontend.
//
// The SDK (~2.5 MB of source across @microsoft/applicationinsights-*) is
// imported dynamically so it lands in its own chunk and never delays the
// first paint of the public landing page. The track*/set* helpers below are
// no-ops until the SDK has loaded, exactly as they already were before
// initializeAppInsights() ran.

let appInsights = null;
let reactPlugin = null;

export async function initializeAppInsights() {
  const connectionString = process.env.REACT_APP_APPLICATIONINSIGHTS_CONNECTION_STRING;

  if (!connectionString) {
    console.log('[AppInsights] No connection string found - frontend monitoring disabled');
    return null;
  }

  try {
    const [{ ApplicationInsights }, { ReactPlugin }, { createBrowserHistory }] = await Promise.all([
      import('@microsoft/applicationinsights-web'),
      import('@microsoft/applicationinsights-react-js'),
      import('history'),
    ]);
    const browserHistory = createBrowserHistory();
    reactPlugin = new ReactPlugin();

    appInsights = new ApplicationInsights({
      config: {
        connectionString,
        extensions: [reactPlugin],
        extensionConfig: {
          [reactPlugin.identifier]: { history: browserHistory }
        },
        enableAutoRouteTracking: true, // Track page views automatically
        disableFetchTracking: false,   // Track fetch API calls
        disableAjaxTracking: false,    // Track XMLHttpRequest calls
        enableCorsCorrelation: true,   // Correlate with backend requests
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        autoTrackPageVisitTime: true,  // Track time spent on pages
        disableExceptionTracking: false
      }
    });

    appInsights.loadAppInsights();

    // Set cloud role for identification
    appInsights.addTelemetryInitializer((envelope) => {
      if (envelope.tags) {
        envelope.tags['ai.cloud.role'] = 'finance-forecaster-web';
      }
      // Redact auth tokens from request/response header telemetry
      const props = envelope.baseData?.properties;
      if (props) {
        for (const key of Object.keys(props)) {
          const lk = key.toLowerCase();
          if (lk.includes('x-auth-token') || lk.includes('authorization')) {
            props[key] = '[REDACTED]';
          }
        }
      }
    });

    console.log('[AppInsights] Frontend Application Insights initialized');

    return appInsights;
  } catch (error) {
    console.error('[AppInsights] Failed to initialize frontend:', error.message);
    return null;
  }
}

/**
 * Track a custom event
 * @param {string} name - Event name
 * @param {object} properties - Custom properties
 */
export function trackEvent(name, properties = {}) {
  if (appInsights) {
    appInsights.trackEvent({ name }, properties);
  }
}

/**
 * Track an exception
 * @param {Error} error - The error object
 * @param {object} properties - Additional context
 */
export function trackException(error, properties = {}) {
  if (appInsights) {
    appInsights.trackException({ exception: error, properties });
  }
}

/**
 * Track a page view
 * @param {string} name - Page name
 * @param {string} uri - Page URI
 */
export function trackPageView(name, uri) {
  if (appInsights) {
    appInsights.trackPageView({ name, uri });
  }
}

/**
 * Set authenticated user for better tracking
 * @param {string} userId - User ID
 */
export function setAuthenticatedUser(userId) {
  if (appInsights) {
    appInsights.setAuthenticatedUserContext(userId, userId, true);
  }
}

/**
 * Clear user context (on logout)
 */
export function clearAuthenticatedUser() {
  if (appInsights) {
    appInsights.clearAuthenticatedUserContext();
  }
}

/**
 * Track a custom metric
 * @param {string} name - Metric name
 * @param {number} average - Average value
 */
export function trackMetric(name, average) {
  if (appInsights) {
    appInsights.trackMetric({ name, average });
  }
}

/**
 * Flush pending telemetry
 */
export function flush() {
  if (appInsights) {
    appInsights.flush();
  }
}

export function getAppInsights() {
  return appInsights;
}

export function getReactPlugin() {
  return reactPlugin;
}
